/**
 * 移动端店铺设置页（增强版）
 * 包含：通用、运费、支付、邮件、通知模板
 */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Skeleton,
  Dialog,
  TextArea,
  Tabs,
  Switch,
  List,
  Selector,
} from 'antd-mobile';
import { adminSettingApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '../../components/RichTextEditor';

export default function MobileSettingPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  // 各 Tab 的 form
  const [generalForm] = Form.useForm();
  const [shippingForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [telegramForm] = Form.useForm();
  const [announcementForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  // 邮件模板列表
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await adminSettingApi.getSettings();
      // 通用设置
      generalForm.setFieldsValue({
        storeName: data?.storeName,
        description: data?.description,
        currency: data?.currency,
        email: data?.email,
        phone: data?.phone,
        address: data?.address,
      });
      // 运费设置
      shippingForm.setFieldsValue({
        shippingBaseFee: data?.shippingBaseFee,
        freeShippingThreshold: data?.freeShippingThreshold,
      });
      // 支付设置
      paymentForm.setFieldsValue({
        nihaoPayToken: data?.nihaoPayToken,
        nihaoPayEnabled: data?.nihaoPayEnabled || false,
      });
      // 邮件设置
      emailForm.setFieldsValue({
        smtpHost: data?.smtpHost,
        smtpPort: data?.smtpPort,
        smtpUsername: data?.smtpUsername,
        smtpPassword: data?.smtpPassword,
        smtpFrom: data?.smtpFrom,
      });
      // Telegram 设置
      telegramForm.setFieldsValue({
        telegramBotToken: data?.telegramBotToken,
        telegramChatId: data?.telegramChatId,
        telegramEnabled: data?.telegramEnabled || false,
      });
      // 公告设置
      announcementForm.setFieldsValue({
        announcement: data?.announcement,
        announcementEnabled: data?.announcementEnabled === true ? [true] : [false],
        maintenanceEnabled: data?.maintenanceEnabled === true ? [true] : [false],
        maintenanceMessage: data?.maintenanceMessage,
      });
    } catch (err) {
      Toast.show('获取设置失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await adminSettingApi.getEmailTemplates();
      setEmailTemplates(data || []);
    } catch (err) {
      console.error('获取邮件模板失败:', err);
    }
  };

  const handleSaveGeneral = async (values) => {
    try {
      await adminSettingApi.updateSettings(values);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleSaveShipping = async (values) => {
    try {
      await adminSettingApi.updateSettings(values);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleSavePayment = async (values) => {
    try {
      await adminSettingApi.updateSettings(values);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleSaveEmail = async (values) => {
    try {
      await adminSettingApi.updateSettings(values);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleSaveTelegram = async (values) => {
    try {
      await adminSettingApi.updateSettings(values);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleSaveAnnouncement = async (values) => {
    try {
      const payload = {
        announcement: values.announcement,
        announcementEnabled: values.announcementEnabled?.[0] === true,
        maintenanceEnabled: values.maintenanceEnabled?.[0] === true,
        maintenanceMessage: values.maintenanceMessage,
      };
      await adminSettingApi.updateSettings(payload);
      Toast.show('保存成功');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    }
  };

  const handleTestTelegram = async () => {
    try {
      const result = await adminSettingApi.testTelegram();
      if (result.success) {
        Toast.show(result.message || '测试消息已发送');
      } else {
        Toast.show(result.message || '发送失败');
      }
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发送失败');
    }
  };

  const openTemplateForm = (template) => {
    setEditingTemplate(template);
    if (template) {
      templateForm.setFieldsValue({
        eventType: template.eventType,
        recipient: template.recipient,
        subject: template.subject,
        bodyHtml: template.bodyHtml,
        enabled: template.enabled,
      });
    }
    setTemplateModalVisible(true);
  };

  const handleSaveTemplate = async (values) => {
    try {
      if (editingTemplate) {
        await adminSettingApi.updateEmailTemplate(editingTemplate.id, values);
        Toast.show('更新成功');
      } else {
        await adminSettingApi.createEmailTemplate(values);
        Toast.show('创建成功');
      }
      setTemplateModalVisible(false);
      fetchTemplates();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '操作失败');
    }
  };

  const handleLogout = () => {
    Dialog.confirm({
      content: '确定要退出登录吗？',
      onConfirm: async () => {
        await logout();
        navigate('/admin/m/login');
      },
    });
  };

  const tabs = [
    { key: 'general', title: '通用' },
    { key: 'announcement', title: '公告' },
    { key: 'shipping', title: '运费' },
    { key: 'payment', title: '支付' },
    { key: 'email', title: '邮件' },
    { key: 'telegram', title: 'Telegram' },
    { key: 'templates', title: '模板' },
  ];

  if (loading) return <div style={{ padding: 16 }}><Skeleton.Title rows={5} /></div>;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* 顶部 Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{
          '--title-font-size': '15px',
          '--active-title-color': '#1677ff',
          '--active-line-color': '#1677ff',
        }}
      >
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.key} title={tab.title} />
        ))}
      </Tabs>

      {/* 各 Tab 内容 */}
      <div style={{ padding: 16 }}>
        {/* 通用设置 */}
        {activeTab === 'general' && (
          <Card>
            <Form
              form={generalForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => generalForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSaveGeneral}
            >
              <Form.Item
                name="storeName"
                label="店铺名称"
                rules={[{ required: true, message: '请输入店铺名称' }]}
              >
                <Input placeholder="请输入店铺名称" />
              </Form.Item>

              <Form.Item name="description" label="店铺描述">
                <TextArea placeholder="请输入店铺描述" rows={3} />
              </Form.Item>

              <Form.Item name="currency" label="货币" initialValue="USD">
                <Selector
                  options={[
                    { label: 'USD', value: 'USD' },
                    { label: 'CNY', value: 'CNY' },
                  ]}
                />
              </Form.Item>

              <Form.Item name="email" label="联系邮箱">
                <Input placeholder="请输入联系邮箱" type="email" />
              </Form.Item>

              <Form.Item name="phone" label="联系电话">
                <Input placeholder="请输入联系电话" />
              </Form.Item>

              <Form.Item name="address" label="联系地址">
                <TextArea placeholder="请输入联系地址" rows={2} />
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* 公告设置 */}
        {activeTab === 'announcement' && (
          <Card>
            <Form
              form={announcementForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => announcementForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSaveAnnouncement}
            >
              <Form.Item name="announcementEnabled" label="启用公告" initialValue={[false]}>
                <Selector
                  options={[
                    { label: '已启用', value: true },
                    { label: '已禁用', value: false },
                  ]}
                />
              </Form.Item>

              <Form.Item name="announcement" label="公告内容">
                <TextArea
                  placeholder="请输入公告内容，如：新年促销活动进行中..."
                  rows={3}
                  maxLength={200}
                />
              </Form.Item>

              <div style={{ fontSize: 12, color: '#999', padding: '8px 0' }}>
                公告将显示在网站前台页面最顶部
              </div>

              <Form.Header>网站维护</Form.Header>

              <Form.Item name="maintenanceEnabled" label="启用维护" initialValue={[false]}>
                <Selector
                  options={[
                    { label: '维护中', value: true },
                    { label: '正常访问', value: false },
                  ]}
                />
              </Form.Item>

              <Form.Item name="maintenanceMessage" label="维护提示语">
                <TextArea
                  placeholder="请输入维护提示语，如：网站正在维护升级..."
                  rows={4}
                  maxLength={500}
                />
              </Form.Item>

              <div style={{ fontSize: 12, color: '#999', padding: '8px 0' }}>
                开启后，前台会显示不可关闭的维护弹窗
              </div>
            </Form>
          </Card>
        )}

        {/* 运费设置 */}
        {activeTab === 'shipping' && (
          <Card>
            <Form
              form={shippingForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => shippingForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSaveShipping}
            >
              <Form.Item name="shippingBaseFee" label="基础运费（元）">
                <Input placeholder="请输入基础运费" type="number" />
              </Form.Item>

              <Form.Item name="freeShippingThreshold" label="免运费门槛（元）">
                <Input placeholder="请输入免运费门槛" type="number" />
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* 支付设置 */}
        {activeTab === 'payment' && (
          <Card>
            <Form
              form={paymentForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => paymentForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSavePayment}
            >
              <Form.Item name="nihaoPayEnabled" label="启用 NihaoPay" initialValue={false}>
                <Switch
                  checkedText="已启用"
                  uncheckedText="已禁用"
                />
              </Form.Item>

              <Form.Item name="nihaoPayToken" label="NihaoPay Token">
                <TextArea
                  placeholder="请输入 NihaoPay API Token"
                  rows={3}
                />
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* 邮件 SMTP 设置 */}
        {activeTab === 'email' && (
          <Card>
            <Form
              form={emailForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => emailForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSaveEmail}
            >
              <Form.Item name="smtpHost" label="SMTP 主机">
                <Input placeholder="如：smtp.gmail.com" />
              </Form.Item>

              <Form.Item name="smtpPort" label="SMTP 端口">
                <Input placeholder="如：587" type="number" />
              </Form.Item>

              <Form.Item name="smtpUsername" label="SMTP 用户名">
                <Input placeholder="请输入邮箱账号" />
              </Form.Item>

              <Form.Item name="smtpPassword" label="SMTP 密码">
                <Input placeholder="请输入邮箱密码或授权码" type="password" />
              </Form.Item>

              <Form.Item name="smtpFrom" label="发件人地址">
                <Input placeholder="请输入发件人邮箱" type="email" />
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Telegram 设置 */}
        {activeTab === 'telegram' && (
          <Card>
            <Form
              form={telegramForm}
              layout="vertical"
              footer={
                <Button
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  onClick={() => telegramForm.submit()}
                >
                  保存设置
                </Button>
              }
              onFinish={handleSaveTelegram}
            >
              <Form.Item name="telegramEnabled" label="启用 Telegram 通知" initialValue={false}>
                <Switch
                  checkedText="已启用"
                  uncheckedText="已禁用"
                />
              </Form.Item>

              <Form.Item name="telegramBotToken" label="Bot Token">
                <Input placeholder="如：123456789:ABCdef..." />
              </Form.Item>

              <Form.Item name="telegramChatId" label="Chat ID">
                <Input placeholder="如：123456789" />
              </Form.Item>

              <div style={{ fontSize: 12, color: '#999', padding: '8px 0' }}>
                配置步骤：
                <ol style={{ paddingLeft: 16, marginTop: 4 }}>
                  <li>在 Telegram 搜索 @BotFather，发送 /newbot</li>
                  <li>获取 Bot Token 和 Chat ID</li>
                </ol>
              </div>

              <Button
                block
                variant="outline"
                size="large"
                style={{ marginTop: 12 }}
                onClick={handleTestTelegram}
              >
                发送测试消息
              </Button>
            </Form>
          </Card>
        )}

        {/* 通知模板管理 */}
        {activeTab === 'templates' && (
          <>
            <Button
              block
              color="primary"
              size="large"
              onClick={() => openTemplateForm(null)}
              style={{ marginBottom: 16 }}
            >
              新建模板
            </Button>

            <List>
              {emailTemplates.map((template) => (
                <List.Item
                  key={template.id}
                  style={{ background: '#fff', marginBottom: 12, padding: '12px 16px' }}
                  description={
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <span style={{ fontSize: 13, color: '#999' }}>
                        {template.eventType}
                      </span>
                      {template.enabled && (
                        <span style={{ fontSize: 13, color: '#52c41a' }}>已启用</span>
                      )}
                    </div>
                  }
                  extra={
                    <span
                      style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => openTemplateForm(template)}
                    >
                      编辑
                    </span>
                  }
                >
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{template.label}</div>
                  <div style={{ fontSize: 13, color: '#999' }}>
                    {template.recipient === 'customer' ? '客户' : '管理员'}
                  </div>
                </List.Item>
              ))}
            </List>
          </>
        )}

        {/* 退出登录 */}
        <Card style={{ marginTop: 16 }}>
          <Button block color="danger" size="large" onClick={handleLogout}>
            退出登录
          </Button>
        </Card>
      </div>

      {/* 编辑模板弹窗 */}
      <Dialog
        visible={templateModalVisible}
        style={{ '--content-width': '95%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingTemplate ? '编辑模板' : '新建模板'}
            </h3>
            <Form
              form={templateForm}
              layout="vertical"
              footer={
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <Button
                    block
                    type="submit"
                    color="primary"
                    onClick={() => templateForm.submit()}
                  >
                    保存
                  </Button>
                  <Button
                    block
                    variant="outline"
                    onClick={() => setTemplateModalVisible(false)}
                  >
                    取消
                  </Button>
                </div>
              }
              onFinish={handleSaveTemplate}
            >
              <Form.Item name="eventType" label="事件类型">
                <Selector
                  options={[
                    { label: '订单确认（客户）', value: 'order_confirmed_customer' },
                    { label: '订单确认（管理员）', value: 'order_confirmed_admin' },
                    { label: '订单取消', value: 'order_cancelled' },
                    { label: '订单发货', value: 'order_shipped' },
                    { label: '欢迎通知', value: 'welcome' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="recipient" label="接收人">
                <Selector
                  options={[
                    { label: '客户', value: 'customer' },
                    { label: '管理员', value: 'admin' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="subject" label="邮件主题">
                <Input placeholder="请输入邮件主题" />
              </Form.Item>
              <Form.Item name="bodyHtml" label="邮件内容">
                <RichTextEditor simple placeholder="邮件正文，支持富文本；变量如 {{customerName}}、{{orderNo}}" />
              </Form.Item>
              <Form.Item name="enabled" label="启用" initialValue={true}>
                <Selector
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                />
              </Form.Item>
            </Form>
          </div>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}
